import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { retryTransientErrorOnce } from "@/lib/query-client-defaults";
import {
	type AgentUpdateResponse,
	type ApproveDeviceInput,
	approveDevice,
	type ConnectionPolicy,
	type ConnectionSecret,
	type CreateEnrollmentTokenInput,
	type CreateRemoteSessionInput,
	createEnrollmentToken,
	createRemoteSession,
	type DbOnlineResponse,
	type DeviceAgent,
	type DeviceDiagnosticsResponse,
	type DeviceMetricsSummary,
	type DeviceNetworkTests,
	type DeviceNote,
	type DeviceOperation,
	type DeviceShareHealth,
	deleteDevice,
	disableGroupExclusion,
	disableNotificationOptin,
	downloadLogBundle,
	type EnrollmentTokenResult,
	type EnrollmentTokenSummary,
	enableGroupExclusion,
	enableNotificationOptin,
	type GroupExclusionEntry,
	getConnectionSecret,
	getDeviceDetail,
	getDeviceDiagnostics,
	getDeviceMetricsSummary,
	getDeviceNetworkTests,
	getDeviceShareHealth,
	getInstallerMetadata,
	getLiteInstallerMetadata,
	getLogBundleStatus,
	getRemoteSession,
	grantDeviceKsShareAccess,
	type InstallerMetadata,
	type KsShareFixResponse,
	killDeviceOffice,
	type LogBundle,
	type LogBundleStatus,
	leaveDeviceNote,
	listDeviceNotes,
	listDeviceOperations,
	listDevices,
	listEnrollmentTokens,
	listGroupExclusions,
	listNotificationOptin,
	listRecentConnections,
	type NetworkTestDays,
	type NotificationOptinEntry,
	type OfficeKillResponse,
	type OptinKind,
	type PurgeRustdeskResponse,
	purgeDeviceRustdesk,
	type RecentConnection,
	type RemoteSession,
	type RemoteSessionOutcome,
	type RotatePasswordResponse,
	type RunOperationResponse,
	recoverDevice,
	requestAgentUpdate,
	requestLogBundle,
	restartDeviceSpooler,
	retargetDeviceApman,
	revokeDevice,
	revokeEnrollmentToken,
	rotateRustdeskPassword,
	runDeviceOperation,
	type SpoolerRestartResponse,
	type StartNetworkTestResponse,
	setDeviceDbOnline,
	startDeviceNetworkTest,
	type UnlockServiceAccountResponse,
	type UpdateDeviceAssignmentInput,
	unlockDeviceServiceAccount,
	updateAsystentEnabled,
	updateDeviceAssignment,
	updateDeviceCanary,
	updateDeviceConnectionPolicy,
	updateDeviceOperatorNote,
} from "./devices-api";
import { devicesRefetchInterval } from "./devices-helpers";
import { CAMPAIGNS_QUERY_KEY } from "./use-campaigns-query";

export const DEVICES_QUERY_KEY = ["device-agent", "devices"] as const;
export const DEVICE_DETAIL_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-detail", uuid] as const;
export const DEVICE_DIAGNOSTICS_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-diagnostics", uuid] as const;
export const DEVICE_METRICS_SUMMARY_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-metrics-summary", uuid] as const;
/** Prefiks bez `days` — unieważnia wszystkie zakresy serii jednego urządzenia. */
export const DEVICE_NETWORK_TESTS_PREFIX_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-network-tests", uuid] as const;
export const DEVICE_NETWORK_TESTS_QUERY_KEY = (uuid: string, days: NetworkTestDays) =>
	[...DEVICE_NETWORK_TESTS_PREFIX_QUERY_KEY(uuid), days] as const;
export const DEVICE_NOTES_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-notes", uuid] as const;
/** Prefiks bez przypisania — unieważnia stan udziału urządzenia w każdej aptece. */
export const DEVICE_SHARE_HEALTH_PREFIX_QUERY_KEY = (uuid: string) =>
	["device-agent", "device-share-health", uuid] as const;
/**
 * Backend zwraca stan udziału wyłącznie z BIEŻĄCEGO przypisania urządzenia, więc
 * przypisanie (`tenantId`, `idfirm`) jest częścią klucza: po przeniesieniu do
 * innej apteki kafel nie pokazuje stanu poprzedniej z cache.
 */
export const DEVICE_SHARE_HEALTH_QUERY_KEY = (
	uuid: string,
	tenantId: number | null,
	idfirm: number | null,
) => [...DEVICE_SHARE_HEALTH_PREFIX_QUERY_KEY(uuid), tenantId, idfirm] as const;
export const REMOTE_SESSION_QUERY_KEY = (sessionId: string) =>
	["device-agent", "remote-session", sessionId] as const;
export const INSTALLER_METADATA_QUERY_KEY = ["device-agent", "installer-metadata"] as const;
export const LITE_INSTALLER_METADATA_QUERY_KEY = [
	"device-agent",
	"lite-installer-metadata",
] as const;
export const ENROLLMENT_TOKENS_QUERY_KEY = ["device-agent", "enrollment-tokens"] as const;
export const LOG_BUNDLE_QUERY_KEY = (deviceUuid: string, bundleId: string) =>
	["device-agent", "log-bundle", deviceUuid, bundleId] as const;

/**
 * Terminal outcomes stop the status poll — nothing more will change once the
 * session has connected or failed/cancelled/timed out. `preparing`/`pending`/
 * `launched` are still in-flight and keep polling.
 */
export function isTerminalOutcome(outcome: RemoteSessionOutcome | undefined): boolean {
	return (
		outcome === "connected" ||
		outcome === "failed" ||
		outcome === "cancelled" ||
		outcome === "timeout"
	);
}

/** Poll cadence for the remote-session status query (ms). */
export const REMOTE_SESSION_POLL_MS = 2000;
/**
 * Hard ceiling on how long the UI keeps polling a non-terminal session before
 * giving up. Must comfortably exceed the backend `PREPARE_TIMEOUT_SECS` (60s)
 * so the backend's own `preparing → timeout` transition is observed rather than
 * the client bailing first — 80s = 60s prepare budget + ~20s launch/connect
 * slack.
 */
export const REMOTE_SESSION_MAX_POLL_MS = 80_000;

/**
 * Live devices list for „Zdalny dostęp". Unlike the app-wide defaults
 * (staleTime 30min, no focus refetch), this view must reflect reality within
 * seconds: a freshly-enrolled device's approval card and a just-provisioned
 * RustDesk ID both arrive asynchronously after the agent connects, so without
 * polling the operator only saw them after a hard F5. We poll fast while
 * something is mid-onboarding (see `devicesRefetchInterval`), keep a short
 * staleTime, and refetch on window focus + reconnect.
 */
export function useDevicesQuery() {
	return useQuery<DeviceAgent[]>({
		queryKey: DEVICES_QUERY_KEY,
		queryFn: listDevices,
		staleTime: 5_000,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchInterval: (query) => devicesRefetchInterval(query.state.data),
	});
}

export const RECENT_CONNECTIONS_QUERY_KEY = ["device-agent", "recent-connections"] as const;

/**
 * The signed-in operator's „Ostatnie połączenia" (max 5, newest first). Cheap,
 * index-backed read. Refetches on window focus + reconnect so that returning to
 * the tab after a remote-desktop session shows the connection just made (the
 * write happens fire-and-forget from „Połącz").
 */
export function useRecentConnectionsQuery() {
	return useQuery<RecentConnection[]>({
		queryKey: RECENT_CONNECTIONS_QUERY_KEY,
		queryFn: listRecentConnections,
		staleTime: 15_000,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

/**
 * One device in the list-item shape (`hubOnline` from the hub). Polled at the
 * same cadence as the devices list — the helpdesk station panel shows live
 * presence from it.
 */
export function useDeviceDetailQuery(uuid: string, enabled: boolean) {
	return useQuery<DeviceAgent>({
		queryKey: DEVICE_DETAIL_QUERY_KEY(uuid),
		queryFn: () => getDeviceDetail(uuid),
		enabled: enabled && !!uuid,
		staleTime: 5_000,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchInterval: (query) =>
			devicesRefetchInterval(query.state.data ? [query.state.data] : undefined),
	});
}

/**
 * Live-or-cached diagnostics for one device. Fetched only when a tile expands.
 * staleTime ~20s + dedupe so collapse/expand toggling does not hammer the
 * device. Single retry of transient errors only (network, 5xx, 408, 429): the
 * backend already has an ~8s wall-clock timeout and returns a `source` rather
 * than throwing, so retries add little.
 */
export function useDeviceDiagnosticsQuery(uuid: string, enabled: boolean) {
	return useQuery<DeviceDiagnosticsResponse>({
		queryKey: DEVICE_DIAGNOSTICS_QUERY_KEY(uuid),
		queryFn: () => getDeviceDiagnostics(uuid),
		enabled: enabled && !!uuid,
		staleTime: 20_000,
		retry: retryTransientErrorOnce,
	});
}

/**
 * Podsumowanie metryk obciążenia (typowe wykorzystanie / szczyty / seria
 * dzienna) — czysty odczyt z DB, dane przyrastają co ~15 min, więc godzinny
 * staleTime wystarcza. Gated na rozwinięciu kafla (`enabled`) — NIGDY dla całej
 * listy ~1000 urządzeń naraz.
 */
export function useDeviceMetricsSummaryQuery(uuid: string, enabled: boolean) {
	return useQuery<DeviceMetricsSummary>({
		queryKey: DEVICE_METRICS_SUMMARY_QUERY_KEY(uuid),
		queryFn: () => getDeviceMetricsSummary(uuid),
		enabled: enabled && !!uuid,
		staleTime: 60 * 60_000,
		retry: retryTransientErrorOnce,
	});
}

/**
 * Stan udziału KS (samonaprawa, apteki własne) — ostatni raport agenta i
 * historia zmian z DB. Agent raportuje przy zmianie stanu, poza tym co 5 min
 * przy usterce, co 30 min gdy zdrowo i co 6 h z serwera, więc minuta wystarcza.
 * Gated na rozwinięciu kafla, nigdy dla całej listy. `tenantId`/`idfirm` =
 * bieżące przypisanie urządzenia (tylko klucz cache, patrz
 * `DEVICE_SHARE_HEALTH_QUERY_KEY`).
 */
export function useDeviceShareHealthQuery(
	uuid: string,
	tenantId: number | null,
	idfirm: number | null,
	enabled: boolean,
) {
	return useQuery<DeviceShareHealth>({
		queryKey: DEVICE_SHARE_HEALTH_QUERY_KEY(uuid, tenantId, idfirm),
		queryFn: () => getDeviceShareHealth(uuid),
		enabled: enabled && !!uuid,
		staleTime: 60_000,
		retry: retryTransientErrorOnce,
	});
}

/** Odpytywanie serii, dopóki lokalizacja ma test w toku (test trwa ≤ 45 s). */
export const NETWORK_TEST_POLL_MS = 3_000;
/**
 * Bezpiecznik: najwyżej 4 min odpytywania jednego testu. Musi być dłuższy niż
 * 150 s, po których backend (harmonogram i GET serii) zamyka zgubiony test jako
 * `failed/no_response` — wtedy odpytywanie kończy się samo, bo `pending` znika.
 */
export const NETWORK_TEST_MAX_POLL_MS = 240_000;

export interface NetworkTestPollState {
	testId: number;
	since: number;
}

/**
 * Następny krok odpytywania serii. Czas liczony od chwili, gdy front PIERWSZY
 * raz zobaczył dany test w toku, a nie od `startedAt` z serwera — przesunięty
 * zegar przeglądarki zatrzymałby wtedy odpytywanie od razu albo nigdy.
 */
export function nextNetworkTestPoll(
	pendingTestId: number | null | undefined,
	prev: NetworkTestPollState | null,
	now: number,
): { state: NetworkTestPollState | null; interval: number | false } {
	if (pendingTestId == null) return { state: null, interval: false };
	const state =
		prev && prev.testId === pendingTestId ? prev : { testId: pendingTestId, since: now };
	return {
		state,
		interval: now - state.since >= NETWORK_TEST_MAX_POLL_MS ? false : NETWORK_TEST_POLL_MS,
	};
}

/**
 * Seria pomiarów internetu lokalizacji urządzenia. Gated na rozwinięciu kafla
 * — NIGDY dla całej listy urządzeń. Lokalizacja dostaje punkt co interwał
 * rotacji (domyślnie godzina), więc staleTime 15 min; po „Zmierz teraz” klucz
 * jest unieważniany, a seria odpytywana co 3 s, dopóki test jest `pending`
 * (najwyżej `NETWORK_TEST_MAX_POLL_MS`).
 * `keepPreviousData`: przełączenie zakresu 7/30/90 nie zwija sekcji do
 * wczytywania.
 */
export function useDeviceNetworkTestsQuery(uuid: string, days: NetworkTestDays, enabled: boolean) {
	const pollRef = useRef<NetworkTestPollState | null>(null);
	return useQuery<DeviceNetworkTests>({
		queryKey: DEVICE_NETWORK_TESTS_QUERY_KEY(uuid, days),
		queryFn: () => getDeviceNetworkTests(uuid, days),
		enabled: enabled && !!uuid,
		staleTime: 15 * 60_000,
		retry: retryTransientErrorOnce,
		placeholderData: keepPreviousData,
		refetchInterval: (query) => {
			const next = nextNetworkTestPoll(
				query.state.data?.pending?.testId,
				pollRef.current,
				Date.now(),
			);
			pollRef.current = next.state;
			return next.interval;
		},
	});
}

/**
 * „Zmierz teraz”. Po sukcesie od razu wpisuje test w toku do serii (przycisk
 * blokuje się bez mrugnięcia do czasu refetchu), a po każdym wyniku unieważnia
 * wszystkie zakresy serii urządzenia — także przy 409 `test_in_progress`, żeby
 * front zobaczył cudzy test w toku i zaczął odpytywać.
 */
export function useStartNetworkTestMutation() {
	const qc = useQueryClient();
	return useMutation<StartNetworkTestResponse, Error, string>({
		mutationFn: (deviceUuid) => startDeviceNetworkTest(deviceUuid),
		onSuccess: (res, deviceUuid) => {
			qc.setQueriesData<DeviceNetworkTests>(
				{ queryKey: DEVICE_NETWORK_TESTS_PREFIX_QUERY_KEY(deviceUuid) },
				(prev) =>
					prev
						? { ...prev, pending: { testId: res.testId, deviceUuid, startedAt: res.startedAt } }
						: prev,
			);
		},
		onSettled: (_data, _error, deviceUuid) => {
			void qc.invalidateQueries({ queryKey: DEVICE_NETWORK_TESTS_PREFIX_QUERY_KEY(deviceUuid) });
		},
	});
}

/** Restart the Windows Print Spooler; invalidates diagnostics on success. */
export function useRestartSpoolerMutation() {
	const qc = useQueryClient();
	return useMutation<SpoolerRestartResponse, Error, string>({
		mutationFn: (deviceUuid) => restartDeviceSpooler(deviceUuid),
		onSuccess: (data, deviceUuid) => {
			if (data.ok) {
				void qc.invalidateQueries({ queryKey: DEVICE_DIAGNOSTICS_QUERY_KEY(deviceUuid) });
			}
		},
	});
}

export function useKillOfficeMutation() {
	return useMutation<OfficeKillResponse, Error, string>({
		mutationFn: (deviceUuid) => killDeviceOffice(deviceUuid),
	});
}

export function useDbOnlineMutation() {
	return useMutation<DbOnlineResponse, Error, string>({
		mutationFn: (deviceUuid) => setDeviceDbOnline(deviceUuid),
	});
}

/**
 * Podpisany katalog operacji urządzeniowych. Statyczne dane serwera — jeden
 * queryKey wspólny dla wszystkich rozwiniętych kafli, długi `staleTime`, więc
 * pobiera się raz na sesję. `enabled` niesie guard super_admin: endpoint jest
 * `require_super_admin`, więc bez tego non-super_admin dostawałby 403.
 */
export const DEVICE_OPERATIONS_QUERY_KEY = ["device-agent", "operations"] as const;

export function useDeviceOperationsQuery(enabled: boolean) {
	return useQuery<DeviceOperation[]>({
		queryKey: DEVICE_OPERATIONS_QUERY_KEY,
		queryFn: () => listDeviceOperations(),
		enabled,
		staleTime: 60 * 60 * 1000,
	});
}

export function useRunDeviceOperationMutation() {
	const qc = useQueryClient();
	return useMutation<RunOperationResponse, Error, { deviceUuid: string; operationId: string }>({
		mutationFn: ({ deviceUuid, operationId }) => runDeviceOperation(deviceUuid, operationId),
		onSuccess: (_data, { deviceUuid }) => {
			void qc.invalidateQueries({ queryKey: DEVICE_DIAGNOSTICS_QUERY_KEY(deviceUuid) });
		},
	});
}

export function useUnlockServiceAccountMutation() {
	return useMutation<UnlockServiceAccountResponse, Error, string>({
		mutationFn: (deviceUuid) => unlockDeviceServiceAccount(deviceUuid),
	});
}

/** Naprawa udziału KS z kafla; po niej świeży stan udziału zamiast cache. */
export function useKsShareFixMutation(kind: "apman" | "grant") {
	const qc = useQueryClient();
	return useMutation<KsShareFixResponse, Error, string>({
		mutationFn: (deviceUuid) =>
			kind === "apman" ? retargetDeviceApman(deviceUuid) : grantDeviceKsShareAccess(deviceUuid),
		onSuccess: (_data, deviceUuid) => {
			void qc.invalidateQueries({ queryKey: DEVICE_SHARE_HEALTH_PREFIX_QUERY_KEY(deviceUuid) });
		},
	});
}

export function useRotatePasswordMutation() {
	return useMutation<RotatePasswordResponse, Error, string>({
		mutationFn: (deviceUuid) => rotateRustdeskPassword(deviceUuid),
	});
}

/**
 * Czysta reinstalacja RustDeska. Po sukcesie ID i hasło znikają (agent stawia
 * je na nowo), więc odświeżamy listę urządzeń i diagnostykę.
 */
export function usePurgeRustdeskMutation() {
	const qc = useQueryClient();
	return useMutation<PurgeRustdeskResponse, Error, string>({
		mutationFn: (deviceUuid) => purgeDeviceRustdesk(deviceUuid),
		onSuccess: (data, deviceUuid) => {
			if (!data.ok) return;
			// Backend wyzerował ID przy czystce — nadpisujemy je w cache OD RAZU,
			// nie czekając na refetch. Bez tego panel jeszcze przez moment widział
			// ID sprzed czystki i uznawał, że instalacja już wróciła: blokada
			// przycisku znikała natychmiast (zgłoszone 2026-08-18).
			qc.setQueryData<DeviceAgent[]>(DEVICES_QUERY_KEY, (old) =>
				old?.map((d) => (d.deviceUuid === deviceUuid ? { ...d, rustdeskId: null } : d)),
			);
			qc.setQueryData<DeviceAgent>(DEVICE_DETAIL_QUERY_KEY(deviceUuid), (old) =>
				old ? { ...old, rustdeskId: null } : old,
			);
			void qc.invalidateQueries({ queryKey: DEVICE_DIAGNOSTICS_QUERY_KEY(deviceUuid) });
			void qc.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
		},
	});
}

export interface RequestAgentUpdateVariables {
	deviceUuid: string;
	/** Wersja z półki, którą operator widział i potwierdził w dialogu. */
	expectedVersion: string;
}

/**
 * Aktualizacja agenta na jednym urządzeniu (poza etapem canary). Po sukcesie
 * odświeżamy kafel (wersja agenta, diagnostyka) i listę kampanii — backend
 * zapisuje tę aktualizację jako osobny wiersz kampanii z postępem.
 */
export function useRequestAgentUpdateMutation() {
	const qc = useQueryClient();
	return useMutation<AgentUpdateResponse, Error, RequestAgentUpdateVariables>({
		mutationFn: ({ deviceUuid, expectedVersion }) =>
			requestAgentUpdate(deviceUuid, expectedVersion),
		onSuccess: (_data, { deviceUuid }) => {
			void qc.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			void qc.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(deviceUuid) });
			void qc.invalidateQueries({ queryKey: DEVICE_DIAGNOSTICS_QUERY_KEY(deviceUuid) });
		},
		// Najczęstszy 409 to „wersja na półce zmieniła się” — bez tego przycisk przez
		// 5 min (staleTime metadanych) wysyłałby wciąż starą expectedVersion.
		onError: () => {
			void qc.invalidateQueries({ queryKey: INSTALLER_METADATA_QUERY_KEY });
		},
		// Także po błędzie: gdy wysyłka padnie już po utworzeniu kampanii, backend
		// zostawia jej wiersz z wynikiem `failed` — lista ma go pokazać.
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

/**
 * Historia notatek urządzenia. Status odczytu (`readAt`) ustawia się
 * asynchronicznie, gdy odbiorca zamknie kartkę na hoście (read-receipt → WS).
 * Żeby panel nie wymagał F5: gdy popover otwarty (`enabled`), odpytujemy świeżo
 * (staleTime 0) i pollujemy co 5 s — „Odczytano" pojawia się sam. Po zamknięciu
 * popovera query jest wyłączone, więc nie pollujemy w tle dla każdego kafelka.
 */
export function useDeviceNotesQuery(uuid: string, enabled: boolean) {
	return useQuery<DeviceNote[]>({
		queryKey: DEVICE_NOTES_QUERY_KEY(uuid),
		queryFn: () => listDeviceNotes(uuid),
		enabled,
		staleTime: 0,
		refetchInterval: enabled ? 5000 : false,
	});
}

export function useLeaveNoteMutation() {
	const qc = useQueryClient();
	return useMutation<DeviceNote, Error, { deviceUuid: string; body: string }>({
		mutationFn: ({ deviceUuid, body }) => leaveDeviceNote(deviceUuid, body),
		onSuccess: (_data, { deviceUuid }) => {
			void qc.invalidateQueries({ queryKey: DEVICE_NOTES_QUERY_KEY(deviceUuid) });
		},
	});
}

/**
 * Polls a single remote session every 2s until it reaches a terminal outcome.
 * staleTime: 0 so each tick re-fetches; `enabled` lets the caller start polling
 * only after a session id exists.
 *
 * Polling stops on a terminal outcome OR once `REMOTE_SESSION_MAX_POLL_MS` has
 * elapsed since the query first started fetching — the latter is a safety net
 * for a session that never reaches a terminal state (e.g. backend wedged in
 * `preparing`). The window is wide enough (80s) that the backend's own 60s
 * prepare-timeout fires first under normal operation.
 */
export function useRemoteSessionQuery(sessionId: string | null, enabled: boolean) {
	return useQuery<RemoteSession>({
		queryKey: REMOTE_SESSION_QUERY_KEY(sessionId ?? ""),
		queryFn: () => getRemoteSession(sessionId as string),
		enabled: enabled && !!sessionId,
		staleTime: 0,
		refetchInterval: (query) =>
			isTerminalOutcome(query.state.data?.outcome) ? false : REMOTE_SESSION_POLL_MS,
	});
}

export interface UpdateConnectionPolicyInput {
	deviceUuid: string;
	policy: ConnectionPolicy | null;
}

/**
 * Sets (or clears) the per-device connection-policy override and refreshes the
 * devices list so the resolved `effectiveConnectionPolicy` re-renders.
 */
export function useUpdateConnectionPolicy() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, UpdateConnectionPolicyInput>({
		mutationFn: ({ deviceUuid, policy }) => updateDeviceConnectionPolicy(deviceUuid, policy),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

export interface UpdateCanaryInput {
	deviceUuid: string;
	isCanary: boolean;
}

/**
 * Toggles the per-device CANARY flag and refreshes the devices list so the
 * badge / campaign targeting reflect it immediately.
 */
export function useUpdateDeviceCanary() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, UpdateCanaryInput>({
		mutationFn: ({ deviceUuid, isCanary }) => updateDeviceCanary(deviceUuid, isCanary),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

export const NOTIFICATION_OPTIN_QUERY_KEY = ["device-agent", "notification-optin"] as const;

// Dla `notification-optin` to dawny klucz `[...NOTIFICATION_OPTIN_QUERY_KEY, tenantId]`.
const notificationOptinQueryKey = (
	tenantId: number | null,
	kind: OptinKind = "notification-optin",
) => ["device-agent", kind, tenantId] as const;

/**
 * Opt-in franczyz do kanału powiadomień — JEDNA lista per organizacja
 * URZĄDZENIA (zgoda jest cechą apteki w tej organizacji), więc kafle tej samej
 * apteki czytają z tego samego cache i stan checkboxa jest na nich spójny.
 * Organizacja pochodzi z kafla, nie z przełącznika: panel pokazuje
 * super_adminowi urządzenia wszystkich organizacji naraz. `kind` wybiera
 * zgodę: powiadomienia albo asystę informatyczną (zgłoszenia ze stanowisk).
 */
export function useNotificationOptin(
	tenantId: number | null,
	kind: OptinKind = "notification-optin",
) {
	return useQuery<NotificationOptinEntry[]>({
		queryKey: notificationOptinQueryKey(tenantId, kind),
		queryFn: () => listNotificationOptin(tenantId as number, kind),
		enabled: tenantId != null,
		staleTime: 60_000,
	});
}

/**
 * Włącza/wyłącza opt-in APTEKI (`idfirm`) w organizacji urządzenia
 * (`tenantId`). Zwraca wpis przy włączeniu (z `activeDevices` do ostrzeżenia
 * „0 stanowisk”), `null` przy wyłączeniu.
 */
export function useSetNotificationOptin(kind: OptinKind = "notification-optin") {
	const queryClient = useQueryClient();
	return useMutation<
		NotificationOptinEntry | null,
		Error,
		{ tenantId: number; idfirm: number; enabled: boolean }
	>({
		mutationFn: ({ tenantId, idfirm, enabled }) =>
			enabled
				? enableNotificationOptin(tenantId, idfirm, undefined, kind)
				: disableNotificationOptin(tenantId, idfirm, kind).then(() => null),
		onSuccess: (_result, { tenantId }) => {
			queryClient.invalidateQueries({ queryKey: notificationOptinQueryKey(tenantId, kind) });
		},
	});
}

export const GROUP_EXCLUSION_QUERY_KEY = ["device-agent", "group-exclusion"] as const;

const groupExclusionQueryKey = (tenantId: number | null) =>
	[...GROUP_EXCLUSION_QUERY_KEY, tenantId] as const;

/**
 * Apteki wykluczone z grupy — JEDNA lista per organizacja URZĄDZENIA, jak
 * opt-in powiadomień: wszystkie kafle tej apteki (także zwinięte, z plakietką)
 * czytają ten sam cache, więc stan jest na nich spójny.
 */
export function useGroupExclusions(tenantId: number | null) {
	return useQuery<GroupExclusionEntry[]>({
		queryKey: groupExclusionQueryKey(tenantId),
		queryFn: () => listGroupExclusions(tenantId as number),
		enabled: tenantId != null,
		staleTime: 60_000,
	});
}

/** Wyklucza (`excluded=true`) albo przywraca aptekę do grupy. */
export function useSetGroupExclusion() {
	const queryClient = useQueryClient();
	return useMutation<
		GroupExclusionEntry | null,
		Error,
		{ tenantId: number; idfirm: number; excluded: boolean }
	>({
		mutationFn: ({ tenantId, idfirm, excluded }) =>
			excluded
				? enableGroupExclusion(tenantId, idfirm)
				: disableGroupExclusion(tenantId, idfirm).then(() => null),
		onSuccess: (result, { tenantId, idfirm }) => {
			// Potwierdzony zapis od razu w cache — gdyby odświeżenie listy
			// zawiodło, checkbox i plakietka i tak pokażą stan z serwera.
			queryClient.setQueryData<GroupExclusionEntry[]>(groupExclusionQueryKey(tenantId), (prev) => {
				const rest = (prev ?? []).filter((entry) => entry.idfirm !== idfirm);
				return result ? [result, ...rest] : rest;
			});
			queryClient.invalidateQueries({ queryKey: groupExclusionQueryKey(tenantId) });
		},
	});
}

export function useUpdateAsystentEnabled() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, { deviceUuid: string; asystentEnabled: boolean }>({
		mutationFn: ({ deviceUuid, asystentEnabled }) =>
			updateAsystentEnabled(deviceUuid, asystentEnabled),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

export interface UpdateOperatorNoteInput {
	deviceUuid: string;
	operatorNote: string;
}

export function useUpdateDeviceOperatorNote() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, UpdateOperatorNoteInput>({
		mutationFn: ({ deviceUuid, operatorNote }) =>
			updateDeviceOperatorNote(deviceUuid, operatorNote),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

export interface ApproveDeviceVariables {
	deviceUuid: string;
	body: ApproveDeviceInput;
}

/**
 * Approves a pending device (assign role + scope) and refreshes the devices
 * list so the approval queue drops the row and the assigned device appears.
 */
export function useApproveDeviceMutation() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, ApproveDeviceVariables>({
		mutationFn: ({ deviceUuid, body }) => approveDevice(deviceUuid, body),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

/** Restores a recognized reinstall into its previous device record. */
export function useRecoverDeviceMutation() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, string>({
		mutationFn: recoverDevice,
		onSuccess: (_device, candidateUuid) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.removeQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(candidateUuid) });
		},
	});
}

export interface UpdateDeviceAssignmentVariables {
	deviceUuid: string;
	body: UpdateDeviceAssignmentInput;
}

/**
 * Edytuje przypisanie już zatwierdzonego urządzenia (rola / organizacja /
 * apteka / nazwa) i odświeża listę, by poprawione dane od razu się pokazały.
 * Stan udziału KS zależy od apteki — unieważniamy wszystkie jego wpisy.
 */
export function useUpdateDeviceAssignmentMutation() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, UpdateDeviceAssignmentVariables>({
		mutationFn: ({ deviceUuid, body }) => updateDeviceAssignment(deviceUuid, body),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
			queryClient.invalidateQueries({
				queryKey: DEVICE_SHARE_HEALTH_PREFIX_QUERY_KEY(device.deviceUuid),
			});
		},
	});
}

/**
 * Rejects/revokes a device and refreshes the devices list so it leaves the
 * approval queue.
 */
export function useRevokeDeviceMutation() {
	const queryClient = useQueryClient();
	return useMutation<DeviceAgent, Error, string>({
		mutationFn: (deviceUuid) => revokeDevice(deviceUuid),
		onSuccess: (device) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(device.deviceUuid) });
		},
	});
}

export interface DeleteDeviceVariables {
	deviceUuid: string;
	/** Zlecić agentowi samodeinstalację z hosta (tylko gdy online). */
	uninstall: boolean;
}

/**
 * Trwale usuwa urządzenie z książki adresowej i odświeża listę, by zniknął
 * kafelek. Z `uninstall` zleca też agentowi deinstalację z hosta. Czyści cache
 * szczegółów skasowanego urządzenia.
 */
export function useDeleteDeviceMutation() {
	const queryClient = useQueryClient();
	return useMutation<void, Error, DeleteDeviceVariables>({
		mutationFn: ({ deviceUuid, uninstall }) => deleteDevice(deviceUuid, uninstall),
		onSuccess: (_data, { deviceUuid }) => {
			queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
			queryClient.removeQueries({ queryKey: DEVICE_DETAIL_QUERY_KEY(deviceUuid) });
		},
	});
}

export function useCreateRemoteSession() {
	const queryClient = useQueryClient();
	return useMutation<RemoteSession, Error, CreateRemoteSessionInput>({
		mutationFn: createRemoteSession,
		onSuccess: (session) => {
			// Seed the per-session cache so polling starts from the POST result
			// instead of a null first frame.
			queryClient.setQueryData(REMOTE_SESSION_QUERY_KEY(session.sessionId), session);
		},
	});
}

/**
 * Reveal a device's connection secret (RustDesk ID + unattended password) on
 * demand — super_admin only (backend-enforced). A mutation, not a query, so the
 * secret is fetched ONLY on an explicit click and never auto-cached in a list.
 */
export function useRevealConnectionSecret() {
	return useMutation<ConnectionSecret, Error, string>({
		mutationFn: (deviceUuid: string) => getConnectionSecret(deviceUuid),
	});
}

/**
 * Installer metadata (version/size/sha256). Rarely changes — a generous
 * staleTime keeps the dialog from re-fetching on every open.
 */
export function useInstallerMetadataQuery() {
	return useQuery<InstallerMetadata>({
		queryKey: INSTALLER_METADATA_QUERY_KEY,
		queryFn: getInstallerMetadata,
		staleTime: 5 * 60_000,
	});
}

/** FarmasterAgent Lite installer metadata (non-enrolled computers). */
export function useLiteInstallerMetadataQuery() {
	return useQuery<InstallerMetadata>({
		queryKey: LITE_INSTALLER_METADATA_QUERY_KEY,
		queryFn: getLiteInstallerMetadata,
		staleTime: 5 * 60_000,
	});
}

/** Active enrollment tokens — the list the operator can revoke from. */
export function useEnrollmentTokensQuery() {
	return useQuery<EnrollmentTokenSummary[]>({
		queryKey: ENROLLMENT_TOKENS_QUERY_KEY,
		queryFn: listEnrollmentTokens,
	});
}

export function useCreateEnrollmentToken() {
	const queryClient = useQueryClient();
	return useMutation<EnrollmentTokenResult, Error, CreateEnrollmentTokenInput>({
		mutationFn: createEnrollmentToken,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ENROLLMENT_TOKENS_QUERY_KEY });
		},
	});
}

export function useRevokeEnrollmentToken() {
	const queryClient = useQueryClient();
	return useMutation<void, Error, number>({
		mutationFn: revokeEnrollmentToken,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ENROLLMENT_TOKENS_QUERY_KEY });
		},
	});
}

// ─── Log bundles (Phase 6) ──────────────────────────────────────────────────

/** A log-bundle status is terminal once the tar.gz is ready or collection failed. */
export function isTerminalLogBundleStatus(status: LogBundleStatus | undefined): boolean {
	return status === "ready" || status === "failed";
}

/** Poll cadence for the log-bundle status query (ms). */
export const LOG_BUNDLE_POLL_MS = 1500;
/**
 * Hard ceiling on log-bundle polling. Set above the backend's 5-minute
 * (300s) collection timeout + a buffer so the backend's own
 * `collecting → failed` transition is observed rather than the client bailing
 * first; the ceiling only fires if the backend stops answering entirely.
 */
export const LOG_BUNDLE_MAX_POLL_MS = 330_000;

/**
 * Enqueues a log-bundle request and seeds the per-bundle cache from the POST
 * result so polling starts from `collecting` instead of a null first frame.
 */
export function useRequestLogBundleMutation() {
	const queryClient = useQueryClient();
	return useMutation<LogBundle, Error, string>({
		mutationFn: (deviceUuid) => requestLogBundle(deviceUuid),
		onSuccess: (bundle, deviceUuid) => {
			queryClient.setQueryData(LOG_BUNDLE_QUERY_KEY(deviceUuid, bundle.bundleId), bundle);
		},
	});
}

/**
 * Polls a single log bundle every 1.5s until it reaches a terminal status.
 * `enabled` lets the caller start polling only once a bundle id exists. Polling
 * stops on a terminal status OR after `LOG_BUNDLE_MAX_POLL_MS` as a safety net.
 */
export function useLogBundleStatusQuery(
	deviceUuid: string,
	bundleId: string | null,
	enabled: boolean,
) {
	return useQuery<LogBundle>({
		queryKey: LOG_BUNDLE_QUERY_KEY(deviceUuid, bundleId ?? ""),
		queryFn: () => getLogBundleStatus(deviceUuid, bundleId as string),
		enabled: enabled && !!bundleId,
		staleTime: 0,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (isTerminalLogBundleStatus(data?.status)) return false;
			// Safety net: stop polling if the backend never resolves the bundle
			// (e.g. agent vanished) past the ceiling, measured from creation.
			if (data?.createdAt) {
				const elapsed = Date.now() - new Date(data.createdAt).getTime();
				if (elapsed > LOG_BUNDLE_MAX_POLL_MS) return false;
			}
			return LOG_BUNDLE_POLL_MS;
		},
	});
}

export { downloadLogBundle };
